import * as React from "react"
import { zodResolver } from "@hookform/resolvers/zod"
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { useForm } from "react-hook-form"
import { z } from "zod"

import { MeterStatusType } from "@/types/meter"
import { addMeterStatus, editMeterStatus } from "@/lib/api/meter"
import { useToast } from "@/hooks/use-toast"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { DropdownMenuSeparator } from "@/components/ui/dropdown-menu"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { ComboboxSearch } from "@/components/combobox-search"

import { Icons } from "../icons"
import { Checkbox } from "../ui/checkbox"
import { Input } from "../ui/input"
import { Textarea } from "../ui/textarea"

const formSchema = z.object({
  sequence: z.coerce.number().min(1, "Sequence is required"),
  name: z.string().min(1, "name is required"),
  description: z.string().min(1, "Description is required"),
  icon: z.string().optional(),
})

export default function MeterStatusForm({
  onClose,
  meterStatusData,
  setShowEditMeterStatusModal,
}: {
  onClose?: () => void
  meterStatusData?: MeterStatusType
  setShowEditMeterStatusModal?: React.Dispatch<React.SetStateAction<boolean>>
}) {
  const { toast } = useToast()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: meterStatusData
      ? {
          sequence: meterStatusData.sequence ?? undefined,
          name: meterStatusData.name ?? "",
          description: meterStatusData.description ?? "",
          icon: meterStatusData.icon ?? "",
        }
      : {
          sequence: undefined,
          name: "",
          description: "",
          icon: "",
        },
  })

  const mutation = useMutation({
    mutationFn: async (data: MeterStatusType) => {
      if (meterStatusData?.meter_status_id) {
        await editMeterStatus(data, meterStatusData?.meter_status_id)
      } else {
        await addMeterStatus(data)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["meterStatusList"] })
      toast({
        description: meterStatusData
          ? "Meter Status details have been successfully updated."
          : "New meter status has been successfully added.",
      })
      onClose?.()
      setShowEditMeterStatusModal?.(false)
    },
    onError: async (error: any) => {
      let errorMessage = "Please try again."

      try {
        if (error.message) {
          errorMessage = error.message // Extract message from thrown error
        }
      } catch (err) {
        console.error("Error handling failed response:", err)
      }

      toast({
        title: "Something went wrong!",
        description: errorMessage,
        variant: "destructive",
      })
    },
  })

  function handleSubmit(values: z.infer<typeof formSchema>) {
    console.log("Submitting values:", values)
    mutation.mutate(values as MeterStatusType)
  }

  return (
    <div className="px-2">
      <DropdownMenuSeparator className="mt-4" />
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className="grid grid-cols-1 gap-4 lg:container md:max-w-[1000px]"
        >
          <Card className="my-4 gap-4 p-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Name<span className="text-red-600">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Description<span className="text-red-600">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="sequence"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Sequence<span className="text-red-600">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input type="number" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="icon"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Icon</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </Card>
          <div className="flex flex-row justify-end gap-2 md:mr-4">
            <Button
              variant="outline"
              type="button"
              onClick={() => {
                onClose?.()
                setShowEditMeterStatusModal?.(false)
              }}
              className="w-28"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={mutation.isPending}
              className="w-28"
            >
              {mutation.isPending && (
                <Icons.loader className="mr-2 animate-spin" />
              )}
              {meterStatusData ? `Edit` : `Add`}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
